import { useEffect, useReducer, useState } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import {
    createResourceReducer,
    createInitialResourceState,
} from "@/hooks/reducers/resource.reducer";
import { getInstitutionStudents } from "@/api/dashboardService";
import { parseApiError } from "@/utils/errorHandler";
import type { user } from "@/types/api/user.api";

type studentFilters = { search: string };

const studentReducer = createResourceReducer<user, studentFilters>();
const initialStudentState = createInitialResourceState<user, studentFilters>({
    search: "",
});

export const useStudentListSection = (
    institutionId: number | string | null | undefined,
) => {
    const [state, dispatch] = useReducer(studentReducer, initialStudentState);

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    useEffect(() => {
        dispatch({ type: "SET_FILTERS", payload: { search: debouncedSearchTerm } });
    }, [debouncedSearchTerm]);

    useEffect(() => {
        if (!institutionId) {
            console.warn('No institution ID provided to useStudentListSection');
            dispatch({ type: "SET_DATA", payload: { results: [], count: 0 } });
            return;
        }

        const fetchStudents = async () => {
            dispatch({ type: "FETCH_START" });
            const fetchFilters = { page: state.currentPage, ...state.filters };

            try {
                if (typeof institutionId === 'object') {
                    console.error('Institution ID should not be an object:', institutionId);
                    throw new Error('Invalid institution ID format');
                }

                const payload = await getInstitutionStudents(
                    institutionId,
                    fetchFilters,
                );
                dispatch({ type: "FETCH_SUCCESS" });
                dispatch({
                    type: "SET_DATA",
                    payload: { results: payload.results, count: payload.count },
                });
            } catch (err) {
                dispatch({ type: "FETCH_FAILURE", payload: parseApiError(err) });
            }
        };

        fetchStudents();
    }, [institutionId, state.currentPage, state.filters]);

    const setPage = (page: number) => {
        dispatch({ type: "SET_PAGE", payload: page });
    };

    return {
        // Estado del Recurso
        students: state.data,
        count: state.count,
        currentPage: state.currentPage,
        isLoading: state.isLoading,
        error: state.error,

        // Estado y manejador de la Búsqueda
        searchTerm,
        setSearchTerm,

        // Manejador de Paginación
        setPage,
    };
};
